import { normalizeNumber, phone } from '@loggi/js/formatters';
import { isPhone } from '@loggi/js/validators';
import Button from '@loggi/ui/components/button';
import Col from '@loggi/ui/components/col';
import TextInput from '@loggi/ui/components/text-input';
import View from '@loggi/ui/components/view';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { packageTrackerModel } from 'public-tracker/public-tracker.models/package-tracker.model';
import React from 'react';
// If this library doesn't exist anymore, just remove itself and its functions then it will all work again
import {
  formatPhoneNumber,
  isValidPhoneNumber
} from 'react-phone-number-input';
import {
  FAILURE_CHANGE_MOBILE_PHONE_NUMBER,
  SUCCESS_CHANGE_MOBILE_PHONE_NUMBER
} from 'shared/analytics/analytics.constants';
import sendGoogleAnalyticsDataEvent from 'shared/analytics/analytics.utils';
import callMutation from 'shared/relay/mutations/call-mutation';
import mutation from 'shared/relay/mutations/mobile-phone-notification-subscription.mutation';
import {
  DEFAULT_ERROR_MESSAGE,
  IS_PACKAGE_TRACKING_PHONE_INPUT
} from 'tracker.constants';
import styles from './phone-input.styles.scss';

const MOBILE_NUMBER = 'mobile_number';

export class PhoneInput extends React.Component {
  static propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    packageTracker: PropTypes.shape(packageTrackerModel).isRequired,
    buttonShape: PropTypes.string.isRequired,
    buttonIcon: PropTypes.string.isRequired,
    callbackToRenderTokenComponent: PropTypes.func.isRequired,
    callbackToRenderStatusComponent: PropTypes.func.isRequired,
    changeMobilePhoneNumber: PropTypes.bool,
    // eslint-disable-next-line react/no-unused-prop-types
    mobilePhoneNumber: PropTypes.string
  };

  static defaultProps = {
    changeMobilePhoneNumber: false,
    mobilePhoneNumber: null
  };

  constructor(props) {
    super(props);

    this.state = {
      inputPhoneNumberValue: '',
      onInputPaste: false,
      isLoadingButton: false,
      invalidMobilePhoneError: false,
      invalidMobilePhoneErrorText: '',
      isMobilePhoneNumberAlreadyVerified: false,
      mobilePhoneNumber: get(this.props, 'mobilePhoneNumber', null),
      hasOnFailureMutation: false
    };

    this._handleInputChange = this._handleInputChange.bind(this);
    this._setErrorMessage = this._setErrorMessage.bind(this);
    this._isValidPhoneNumber = this._isValidPhoneNumber.bind(this);
    this._shouldDisableButton = this._shouldDisableButton.bind(this);
    this._successMutation = this._successMutation.bind(this);
    this._failureMutation = this._failureMutation.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onKeyPress = this._onKeyPress.bind(this);
  }

  _sendGoogleAnalyticsEvent = (action, label, value) => {
    sendGoogleAnalyticsDataEvent({ action, label, value });
  };

  _handleInputChange(e) {
    const { onInputPaste } = this.state;
    this.setState({
      invalidMobilePhoneError: false,
      hasOnFailureMutation: false
    });
    const valueWithoutWhitespace = e.target.value.trim();
    let valueWithPhoneFormat;
    if (onInputPaste) {
      valueWithPhoneFormat =
        formatPhoneNumber(valueWithoutWhitespace) ||
        phone(valueWithoutWhitespace);
      this.setState({ onInputPaste: false });
    } else {
      valueWithPhoneFormat = phone(valueWithoutWhitespace);
    }
    this.setState({ inputPhoneNumberValue: valueWithPhoneFormat });
  }

  _setErrorMessage(
    message = IS_PACKAGE_TRACKING_PHONE_INPUT.input.errorMessage
  ) {
    this.setState({
      isLoadingButton: false,
      invalidMobilePhoneError: true,
      invalidMobilePhoneErrorText: message
    });
  }

  _isValidPhoneNumber() {
    const { inputPhoneNumberValue } = this.state;
    if (inputPhoneNumberValue) {
      const phoneBRFormat = `+55${inputPhoneNumberValue}`;
      if (isValidPhoneNumber(phoneBRFormat) || isPhone(phoneBRFormat)) {
        return true;
      }
    }
    return false;
  }

  _shouldDisableButton() {
    const {
      inputPhoneNumberValue,
      mobilePhoneNumber,
      invalidMobilePhoneError,
      hasOnFailureMutation
    } = this.state;

    return (
      (!inputPhoneNumberValue ||
        inputPhoneNumberValue === mobilePhoneNumber ||
        invalidMobilePhoneError) &&
      !hasOnFailureMutation
    );
  }

  _successMutation(response) {
    const {
      success,
      errors,
      shouldValidateToken
    } = response.packageTrackerNotificationSubscription;

    const { callbackToRenderTokenComponent } = this.props;
    const {
      inputPhoneNumberValue,
      isMobilePhoneNumberAlreadyVerified
    } = this.state;

    if (shouldValidateToken) {
      this._sendGoogleAnalyticsEvent(SUCCESS_CHANGE_MOBILE_PHONE_NUMBER);
      callbackToRenderTokenComponent(inputPhoneNumberValue);
      return this.setState({ isLoadingButton: false });
    }

    if (!success) {
      const error = errors[0];
      this._sendGoogleAnalyticsEvent(FAILURE_CHANGE_MOBILE_PHONE_NUMBER);
      return error.field === MOBILE_NUMBER
        ? this._setErrorMessage(error.message)
        : this._setErrorMessage();
    }

    this.setState({ isMobilePhoneNumberAlreadyVerified: true });
    const { callbackToRenderStatusComponent } = this.props;
    return callbackToRenderStatusComponent(
      inputPhoneNumberValue,
      isMobilePhoneNumberAlreadyVerified
    );
  }

  _failureMutation() {
    this._sendGoogleAnalyticsEvent(FAILURE_CHANGE_MOBILE_PHONE_NUMBER);
    this.setState({
      isLoadingButton: false,
      invalidMobilePhoneError: false,
      invalidMobilePhoneErrorText: DEFAULT_ERROR_MESSAGE,
      hasOnFailureMutation: true
    });
  }

  _packageTrackerNotificationSubscriptionMutation(inputPhoneNumberValue) {
    const variables = {
      input: {
        packageId: get(
          this.props,
          'packageTracker.packageInfo.packageId',
          null
        ),
        mobileNumber: normalizeNumber(inputPhoneNumberValue)
      }
    };
    const callbacks = {
      onSuccess: response => this._successMutation(response),
      onFailure: () => this._failureMutation()
    };

    callMutation(mutation, variables, callbacks);
  }

  _onClick() {
    this.setState({ isLoadingButton: true }, () => {
      const { inputPhoneNumberValue } = this.state;
      const phoneNumberIsValid =
        this._isValidPhoneNumber() || inputPhoneNumberValue === '';

      if (!phoneNumberIsValid) {
        return this._setErrorMessage();
      }

      return this._packageTrackerNotificationSubscriptionMutation(
        inputPhoneNumberValue
      );
    });
  }

  _onKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this._onClick();
    }
  }

  render() {
    const {
      invalidMobilePhoneError,
      invalidMobilePhoneErrorText,
      inputPhoneNumberValue,
      isLoadingButton,
      hasOnFailureMutation
    } = this.state;

    const { changeMobilePhoneNumber, buttonShape, buttonIcon } = this.props;

    const errorMessage =
      invalidMobilePhoneError || hasOnFailureMutation
        ? invalidMobilePhoneErrorText
        : '';
    const inputStatus = invalidMobilePhoneError ? 'danger' : '';
    const gutter = changeMobilePhoneNumber ? null : { top: 'normal' };
    const justifyContent = changeMobilePhoneNumber ? 'center' : '';

    const shouldDisableButton = this._shouldDisableButton();

    return (
      <Col flexDirection="row" gutter={gutter} justifyContent={justifyContent}>
        <Col
          size={changeMobilePhoneNumber ? 7 : 6}
          gutter={{ right: 'normal' }}
        >
          <TextInput
            size={changeMobilePhoneNumber ? 'small' : null}
            value={inputPhoneNumberValue}
            type="tel"
            placeholder={IS_PACKAGE_TRACKING_PHONE_INPUT.input.placeholder}
            status={inputStatus}
            inputProps={{
              maxLength: 15,
              onChange: this._handleInputChange,
              onPaste: () => this.setState({ onInputPaste: true }),
              onKeyPress: e =>
                !shouldDisableButton ? this._onKeyPress(e) : null
            }}
          />
          {errorMessage && (
            <View className={styles.errorMessage}>{errorMessage}</View>
          )}
        </Col>
        <Col>
          <Button
            size={changeMobilePhoneNumber ? 'small' : 'regular'}
            isLoading={isLoadingButton}
            disabled={shouldDisableButton}
            shape={buttonShape}
            icon={buttonIcon}
            onClick={this._onClick}
          />
        </Col>
      </Col>
    );
  }
}

export default PhoneInput;
